import React, { useState, useMemo, useEffect } from "react";
import { Checkbox, Input, Select, Modal } from "antd";
import { notification } from "antd";
import { DeleteOutlined } from "@ant-design/icons";

import { storeUser } from "@/store/user";
import { getResponseErrorMessage, getApiBusinessError, ApiBusinessError } from '@/utils/api-service'

import {
  UpOutlined,
  DownOutlined
} from "@ant-design/icons";

import { generateYearOptions } from "@/composable/useDates";

import api from "@/api/index";

import ModalContentRemove from "../modals/ModalContentRemove";

import "./styles/serial-season-section.scss";

interface Props {
  index: number;
  season: any;
  serial_id: string;
  serial: any;

  remove: (season_id: number, index:number) => void;
  update: () => void;
}

const { TextArea } = Input;

function SerialSeasonForm({ index, season, serial_id, serial, remove, update }: Props) {
  const [isOpen, setIsOpen] = useState<boolean>(season.id ? false : true);
  const [name, setName] = useState<string>(season.title);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [year, setYear] = useState<string>(season.release_year || serial.release_year);
  const [sort_order, setSortOrder] = useState<string>(season.sort_order || 1);
  const [description, setDescription] = useState<string>(season.description || '');
  const [textDelete, setTextDelete] = useState<string | null>(null);
  const [isShowModalRemove, setIsShowModalRemove] = useState<boolean>(false);

  const handleSave = async (event) => {
    try {
      event.preventDefault();
      
      if(isDisabled) {
        return;
      }

      const data = {
        title: name,
        description: description || null,
        release_year: year || null,
        sort_order: sort_order,
        status: isActive ? 'active' : 'draft'
      };

      if(!season.id) {
        const response = await api.seasons.create(serial_id, data);

        const businessError = getApiBusinessError(response);
        if (businessError) {
          throw new ApiBusinessError(businessError);
        }
      } else {
        const response = await api.seasons.edit(serial_id, data, season.id);

        const businessError = getApiBusinessError(response);
        if (businessError) {
          throw new ApiBusinessError(businessError);
        }
      }

      update()

      notification.success({
        message: "Сезон сохранен",
      });

    } catch (err) {
      console.error(err);

      const messageFormatter = getResponseErrorMessage(err, 'Ошибка при сохрнение сезона')

      notification.error({
        message: messageFormatter
      });
    }
  };

  const handleToggleView = () => {
    setIsOpen((prev) => !prev);
  };

  const handleRemoveSeason = (event) => {
    event.preventDefault();

    if(season.id) {
      const message = `Вы уверены, что хотите удалить ${season.title}`;
      setTextDelete(message)
      setIsShowModalRemove(true)
      return;
    }

    remove(season.id, index);
  };

  const acceptDelete = () => {
    setIsShowModalRemove(false);
    remove(season.id, index);
  }

  const closeModalRemove = () => {
    setIsShowModalRemove(false);
  }

  const isDisabled = useMemo(() => {
    if(!name) {
      return true
    }

    return false
  }, [name, year, isActive])

  const sectionClassesNane = useMemo(() => {
    const baseClasses = 'serial-season-section';
    const openClasses = isOpen ? 'open' : 'close'

    return `${baseClasses} ${openClasses}`
  }, [isOpen])

  const statusText = useMemo(() => {
    if(!season.id) {
      return 'Активен'
    }

    return season.status ? 'Активен' : 'Черновик'
  }, [season])

  useEffect(() => {
    if(season.status === 'active') {
      setIsActive(true)
    }

    if(!season.id && serial.status === 'active') {
      setIsActive(true)
    }
  }, [])

  return (
    <section className={sectionClassesNane}>
      <div className="serial-season-section__section">
        <div className="serial-season-section__header">
          <a className="serial-season-section__wrap-title" onClick={handleToggleView}>
            <h6 className="serial-season-section__title">{season.title}</h6>
          </a>
          <a
            onClick={handleToggleView}
            className="serial-season-section__toggle-btn"
          >
            {isOpen && <UpOutlined />}
            {!isOpen && <DownOutlined />}
          </a>
        </div>

        {isOpen && (
          <form className="serial-season-section__form">
            <div className="form-group">
              <label className="form-group__label">Название <span className="app-required">*</span></label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Название"
              />
            </div>

            <div className="form-group">
              <label className="form-group__label">
                Описание
              </label>
              <TextArea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                rows={4}
                placeholder="Описание"
              />
            </div>

            <div className="serial-season-section__options-grid">
              <div className="form-group">
                <label className="form-group__label" htmlFor={"Year"}>
                  Год выпуска
                </label>
                <Select
                  id={"Year"}
                  className={"app-select"}
                  value={year}
                  onChange={(val) => setYear(val)}
                  placeholder="Выберите год"
                  options={generateYearOptions()}
                  style={{ width: 300 }}
                  allowClear
                />
              </div>

              <div className="form-group">
                <label className="form-group__label">Номер сезона</label>
                <Input
                    value={sort_order}
                    onChange={(e) => {
                      setSortOrder(e.target.value);
                    }}
                    placeholder="Введите номер сезона"
                />
              </div>

              <div className="form-group form-group--row">
                <label className="form-group__label" htmlFor={"CountEpisodes"}>Кол-во эпизодов:</label>
                <p className="form-group__value">{ season.episodes_count || 0 } </p>
              </div>
            </div>

            <div className={"form-group form-group--checkbox"}>
              <Checkbox
                value="true"
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
              />
              <label className={"form-group-label"}>
                { statusText }
              </label>
            </div>

            <div className="serial-season-section__control">
              {
                !storeUser.isCanRemove && <div></div>
              }

              {
                storeUser.isCanRemove && <button className="app-btn-icon delete" onClick={(event) => handleRemoveSeason(event)}>
                    <DeleteOutlined />
                  </button>
              }

              <button
                className={isDisabled ? 'app-btn disabled' : 'app-btn'}
                onClick={(event) => handleSave(event)}

              >
                Сохранить
              </button>
            </div>
          </form>
        )}
      </div>

      <Modal
        title="Удаление сезона"
        closable={{ "aria-label": "Custom Close Button" }}
        open={isShowModalRemove}
        onOk={acceptDelete}
        onCancel={closeModalRemove}
        okText="Удалить"
        cancelText="Отмена"
      >
        <ModalContentRemove text={textDelete} />
      </Modal>
    </section>
  );
}

export default SerialSeasonForm;
